import { Language, Translations } from './types';
import { en } from './en';
import { es } from './es';

export type { Language, Translations };

export const translations: Record<Language, Translations> = { en, es };

export const LOCALE_BY_LANGUAGE: Record<Language, string> = {
  en: 'en-US',
  es: 'es-US',
};

// Backend-provided labels (tags, spaces) are stored in English; translate at
// display time only, falling back to the original for unknown values.
export const translateTagName = (t: Translations, tagName: string): string =>
  t.tags[tagName] || tagName;

export const translateSpaceName = (t: Translations, slug: string, name: string): string =>
  t.spaces[slug]?.name || name;

export const translateConnectionOption = (t: Translations, option: string): string =>
  t.connectionOptions[option] || option;

export const translateRaceEthnicityOption = (t: Translations, option: string): string =>
  t.raceEthnicityOptions[option] || option;

export const translateSpaceDescription = (
  t: Translations,
  slug: string,
  description: string
): string => t.spaces[slug]?.description || description;

// Map firebase auth error codes to friendly, translated messages. Errors that
// carry an unmapped firebase code get the generic fallback (never the raw
// "Firebase: Error (auth/...)" string); errors without a code (our own custom
// Errors) keep their message.
export const getAuthErrorMessage = (t: Translations, error: any, fallback: string): string => {
  const code: string | undefined = error?.code;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return t.loginForm.authErrors.invalidCredential;
    case 'auth/email-already-in-use':
      return t.loginForm.authErrors.emailAlreadyInUse;
    case 'auth/too-many-requests':
      return t.loginForm.authErrors.tooManyRequests;
    case 'auth/invalid-email':
    case 'auth/missing-email':
      return t.loginForm.authErrors.invalidEmail;
    case 'auth/weak-password':
      return t.loginForm.authErrors.weakPassword;
    case 'auth/network-request-failed':
      return t.loginForm.authErrors.networkFailed;
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/popup-blocked':
      return t.loginForm.authErrors.popupClosed;
    case 'auth/user-disabled':
      return t.loginForm.authErrors.userDisabled;
    default:
      return code ? fallback : error?.message || fallback;
  }
};
